import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, FileDown, FileSpreadsheet, Eye, LockOpen as Unlock2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { addDays, fmtDate, fmtNum, fmtStamp } from "@/lib/format";
import { PageHeader, StatusBadge, LoadingBlock, EmptyState } from "@/components/Shared";
import { TimesheetTable, TimesheetTotals } from "@/components/TimesheetTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FIELD = { location: "Location", miles: "Miles", atv: "ATV", hours: "Hours", minutes: "Minutes" };
const show = (v) => (v === true ? "Yes" : v === false ? "No" : v === null || v === undefined || v === "" ? "blank" : String(v));

function History({ events }) {
  if (!events?.length) return null;
  return (
    <section data-testid="admin-ts-history">
      <h3 className="mb-2 font-display text-lg font-semibold">Change history</h3>
      <ol className="space-y-2">
        {[...events].reverse().map((e, i) => (
          <li key={i} className="rounded-md border border-border p-3 text-sm" data-testid={`admin-ts-history-${i}`}>
            <p><span className="font-semibold capitalize">{e.action}</span> by {e.by_name} · <span className="text-slate-600">{fmtStamp(e.at)}</span></p>
            {e.reason && <p className="mt-1 text-slate-700">Reason: {e.reason}</p>}
            {e.changes?.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-slate-700">
                {e.changes.map((c, j) => <li key={j}>{fmtDate(c.date)} {FIELD[c.field]}: {show(c.from)} → {show(c.to)}</li>)}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

function Unlock({ ts, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (ts.status !== "submitted") return null;
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/timesheets/${ts.id}/unlock`, { reason }); toast.success("Timesheet unlocked"); onDone(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 sm:flex-row" data-testid="admin-ts-unlock">
      <input className="field-input flex-1" placeholder="Reason for unlocking (required)" aria-label="Reason for unlocking" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} data-testid="admin-ts-unlock-reason" />
      <button className="btn-brand h-11 px-4" disabled={busy || reason.trim().length < 3} onClick={go} data-testid="admin-ts-unlock-button"><Unlock2 className="h-4 w-4" />Unlock for edits</button>
    </div>
  );
}

const dl = (t, format) => downloadFile(`/timesheets/${t.id}/export`, { format }).catch((e) => toast.error(errMsg(e)));

function Row({ item, onView }) {
  const t = item.timesheet;
  return (
    <div className="panel flex flex-wrap items-center gap-3 p-4" data-testid={`admin-ts-item-${item.employee_id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-900">{item.employee_name}</p>
        <p className="text-xs text-slate-500">
          {t ? `${fmtNum(t.totals.miles)} mi · ${t.totals.atv_days} ATV days · ${fmtNum(t.totals.hours)} hrs${t.submitted_at ? ` · submitted ${fmtStamp(t.submitted_at)}` : ""}` : "Not started"}
        </p>
      </div>
      {t ? <StatusBadge status={t.status} testId={`admin-ts-status-${item.employee_id}`} /> : <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600" data-testid={`admin-ts-status-${item.employee_id}`}>Missing</span>}
      {t && (
        <div className="flex gap-2">
          <button className="btn-ghost h-10 px-3" onClick={() => onView(t)} data-testid={`admin-ts-view-${item.employee_id}`}><Eye className="h-4 w-4" />View</button>
          <button className="btn-ghost h-10 px-3" onClick={() => dl(t, "pdf")} data-testid={`admin-ts-pdf-${item.employee_id}`}><FileDown className="h-4 w-4" />PDF</button>
          <button className="btn-ghost h-10 px-3" onClick={() => dl(t, "xlsx")} data-testid={`admin-ts-xlsx-${item.employee_id}`}><FileSpreadsheet className="h-4 w-4" />Excel</button>
        </div>
      )}
    </div>
  );
}

export default function AdminTimesheets() {
  const [date, setDate] = useState(null);
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setData(null);
    api.get("/timesheets", { params: date ? { date } : {} }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [date, tick]);

  if (!data) return <LoadingBlock />;
  const done = data.items.filter((i) => i.timesheet?.status === "submitted").length;
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Payroll" title="Timesheets">
        <p className="mt-1 text-sm text-slate-600" data-testid="admin-ts-count">{done} of {data.items.length} submitted</p>
      </PageHeader>
      <div className="panel mb-4 flex items-center justify-between p-2">
        <button className="btn-ghost h-11 w-11 px-0" onClick={() => setDate(addDays(data.period_start, -14))} data-testid="admin-ts-prev" aria-label="Previous period"><ChevronLeft className="h-5 w-5" /></button>
        <p className="font-semibold" data-testid="admin-ts-period">{fmtDate(data.period_start)} – {fmtDate(data.period_end)}</p>
        <button className="btn-ghost h-11 w-11 px-0" onClick={() => setDate(addDays(data.period_start, 14))} data-testid="admin-ts-next" aria-label="Next period"><ChevronRight className="h-5 w-5" /></button>
      </div>
      {!data.items.length ? <EmptyState title="No employees yet" testId="admin-ts-empty" /> : (
        <div className="space-y-2">{data.items.map((i) => <Row key={i.employee_id} item={i} onView={setView} />)}</div>
      )}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="admin-ts-dialog">
          <DialogHeader><DialogTitle>{view?.employee_name} · {view && `${fmtDate(view.period_start)} – ${fmtDate(view.period_end)}`}</DialogTitle></DialogHeader>
          {view && <div className="space-y-3"><Unlock ts={view} onDone={(t) => { setView(t); setTick((n) => n + 1); }} /><TimesheetTotals totals={view.totals} testId="admin-ts-totals" /><TimesheetTable rows={view.rows} /><History events={view.history} /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
